import { Button } from '@latitude/button';
import { Collapsible } from '@latitude/collapsible';
import { IconButton } from '@latitude/icon-button';
import { Icon } from '@latitude/icons';
import { Separator } from '@latitude/separator';
import { Text } from '@latitude/text';
import { cn } from '@latitude/token-utils';
import { useCallback, useState } from 'react';
import type { Ref } from 'react';
import type {
  AlertBannerProps,
  AlertBannerVariant,
  ButtonType,
  ButtonVariant,
} from 'types';

import { alertBannerVariants } from './variants/alert-banner-variants';
import { iconVariants } from './variants/icon-variants';
import { titleVariants } from './variants/title-variants';

const detailsTextClassName =
  'p-spacing-sys-spacing-16 rounded-radius-sys-radius-8 bg-color-ref-transparency-lighten-8';

const AlertBannerIcon = ({
  variant,
  icon,
}: {
  variant: AlertBannerProps['variant'];
  icon: AlertBannerProps['icon'];
}) => {
  if (icon) {
    return icon;
  }

  switch (variant) {
    case 'info':
      return <Icon name="info-circle" />;
    case 'success':
      return <Icon name="check-circle" />;
    case 'warning':
      return <Icon name="alert-circle" />;
    case 'error':
      return <Icon name="alert-octagon" />;
    default:
      return <></>;
  }
};

const AlertBanner = ({
  className,
  variant = 'info',
  icon,
  title,
  description,
  buttonLabel,
  expandable = true,
  expanded,
  detailsText,
  viewDetailsLabel = 'View details',
  onToggle,
  onActionClick,
  onCloseClick,
  isActionLoading,
  actionVariant = 'strong',
  actionType = 'ghost',
  secondaryButtonLabel,
  onSecondaryActionClick,
  isSecondaryActionLoading,
  secondaryButtonVariant = 'strong',
  secondaryButtonType = 'ghost',
  'data-testid': testID = 'alert-banner-container',
  rawProps = {},
  ref,
}: AlertBannerProps & {
  ref?: Ref<HTMLDivElement>;
}) => {
  const [showCollapsible, setShowCollapsible] = useState(expanded || false);

  const handleActionClick = useCallback(() => {
    onActionClick?.();
  }, [onActionClick]);

  const handleSecondaryActionClick = useCallback(() => {
    onSecondaryActionClick?.();
  }, [onSecondaryActionClick]);

  const handleCloseClick = useCallback(() => {
    onCloseClick?.();
  }, [onCloseClick]);

  return (
    <div
      className={cn(
        alertBannerVariants({
          variant,
        }),
        className,
      )}
      data-testid={testID}
      ref={ref}
      {...rawProps}
    >
      <div className="p-spacing-sys-spacing-16 gap-spacing-sys-spacing-8">
        <div className="flex items-center justify-between">
          <div className="gap-spacing-sys-spacing-8 flex items-center">
            <div
              className={cn(
                iconVariants({
                  variant,
                }),
              )}
            >
              <AlertBannerIcon variant={variant} icon={icon} />
            </div>
            <div className="grow">
              <div className="gap-spacing-sys-spacing-8 flex">
                <div>
                  <Text
                    variant="bodyBold"
                    className={cn(titleVariants({ variant }))}
                  >
                    {title}
                  </Text>
                  <Text variant="captionRegular" color="muted">
                    {description}
                  </Text>
                </div>
              </div>
            </div>
          </div>

          {(secondaryButtonLabel || buttonLabel || onCloseClick) && (
            <div className="gap-spacing-sys-spacing-8 flex h-[40px]">
              {secondaryButtonLabel && (
                <Button
                  htmlType="button"
                  variant={secondaryButtonVariant}
                  type={secondaryButtonType}
                  size="medium"
                  onClick={handleSecondaryActionClick}
                  loading={isSecondaryActionLoading}
                >
                  {secondaryButtonLabel}
                </Button>
              )}
              {buttonLabel && (
                <Button
                  htmlType="button"
                  variant={actionVariant}
                  type={actionType}
                  size="medium"
                  onClick={handleActionClick}
                  loading={isActionLoading}
                >
                  {buttonLabel}
                </Button>
              )}
              {onCloseClick && (
                <>
                  <Separator orientation="vertical" />
                  <IconButton
                    htmlType="button"
                    variant="strong"
                    type="ghost"
                    size="medium"
                    aria-label="Close"
                    icon={<Icon name="x-close" />}
                    onClick={handleCloseClick}
                  />
                </>
              )}
            </div>
          )}
        </div>
        {detailsText && (
          <div className="mt-spacing-sys-spacing-8 px-spacing-sys-spacing-32">
            {expandable ? (
              <>
                <div
                  onClick={() => {
                    const next = !showCollapsible;
                    setShowCollapsible(next);
                    onToggle?.(next);
                  }}
                  className="gap-spacing-sys-spacing-8 text-color-comp-button-strong-plain-on-container-enabled flex w-fit cursor-pointer items-center [&>svg]:fill-current"
                >
                  <Text
                    variant="captionRegular"
                    className="text-color-comp-button-strong-plain-on-container-enabled"
                  >
                    {viewDetailsLabel}
                  </Text>
                  {showCollapsible ? (
                    <Icon
                      name="chevron-up"
                      className="w-sizing-sys-size-16 h-sizing-sys-size-16"
                    />
                  ) : (
                    <Icon
                      name="chevron-down"
                      className="w-sizing-sys-size-16 h-sizing-sys-size-16"
                    />
                  )}
                </div>
                <Collapsible open={showCollapsible}>
                  <Text
                    variant="captionRegular"
                    color="strong"
                    className={cn(
                      detailsTextClassName,
                      'mt-spacing-sys-spacing-8',
                    )}
                  >
                    {detailsText}
                  </Text>
                </Collapsible>
              </>
            ) : (
              <Text
                variant="captionRegular"
                color="strong"
                className={detailsTextClassName}
              >
                {detailsText}
              </Text>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

AlertBanner.displayName = 'AlertBanner';

export {
  AlertBanner,
  type AlertBannerProps,
  type AlertBannerVariant,
  type ButtonType,
  type ButtonVariant,
};
